import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { DEFAULT_TZ, formatTimeInZone } from '../../../shared/utils/timezone';
import type { Squad, User } from '../../../types';
import { dateKeyForDateInZone, quarterKeyForDateInZone } from './subbing';

type AvailabilityStatus = 'available' | 'maybe' | 'unavailable';

export type LeadTeacherConflictEvent = {
  eventId: string;
  squadId: string;
  title: string;
  start: Date;
  end: Date;
  dateLabel: string; // squad tz
  teacherDateKey: string; // teacher tz YYYY-MM-DD
  teacherQuarterKey: ReturnType<typeof quarterKeyForDateInZone>;
};

export type LeadTeacherConflictsSummaryRow = {
  dateLabel: string; // squad tz
  count: number;
};

export type LeadTeacherAssignmentAnalysis = {
  teacher: User;
  teacherTimeZone: string;
  hasAnyWeeklyAvailabilityTemplate: boolean;
  conflictEvents: LeadTeacherConflictEvent[];
  conflictsByDate: LeadTeacherConflictsSummaryRow[];
};

export type ChangeSquadLeadResult = {
  updatedEvents: number;
  resolvedCoverage: number;
  preservedCoverage: number;
};

/**
 * The only supported write path for changing a squad's permanent lead.
 * Coverage fields are server-owned, so this must not be implemented as a
 * client-side squad write followed by calendar synchronization.
 */
export async function changeSquadLeadTeacher(params: {
  squadId: string;
  oldTeacherId: string | null;
  newTeacherId: string | null;
}): Promise<ChangeSquadLeadResult> {
  const callable = httpsCallable<typeof params, ChangeSquadLeadResult>(
    getFunctions(),
    'adminChangeSquadLeadTeacher',
  );
  const result = await callable(params);
  return result.data;
}

function normalizeAvailabilityStatus(v: any): AvailabilityStatus | null {
  if (v === 'available' || v === 'maybe' || v === 'unavailable') return v;
  return null;
}

function minutes(t: string): number {
  const [hh, mm] = String(t || '').split(':').map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return NaN;
  return hh * 60 + mm;
}

function overlaps(aStart: number, aEnd: number, bStart: number, bEnd: number): boolean {
  return aStart < bEnd && bStart < aEnd;
}

function fullyCovered(rangeStart: number, rangeEnd: number, evStart: number, evEnd: number): boolean {
  return rangeStart <= evStart && evEnd <= rangeEnd;
}

function dayKeyInZone(d: Date, tz: string): keyof any {
  const dow = formatTimeInZone(d, tz, 'EEE').toLowerCase();
  const map: Record<string, string> = {
    sun: 'sun', mon: 'mon', tue: 'tue', wed: 'wed', thu: 'thu', fri: 'fri', sat: 'sat',
  };
  return (map[dow] || 'mon') as any;
}

function timeOfDayMinutesInZone(d: Date, tz: string): number {
  const hh = Number(formatTimeInZone(d, tz, 'HH'));
  const mm = Number(formatTimeInZone(d, tz, 'mm'));
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return NaN;
  return hh * 60 + mm;
}

function hasAnyWeeklyAvailabilityTemplate(teacher: any): boolean {
  const raw = (teacher?.availabilityTemplates || {}) as any;
  if (!raw || typeof raw !== 'object') return false;
  return Object.values(raw).some((q: any) => q && typeof q === 'object' && q.week);
}

/**
 * Evaluate whether an event conflicts with a teacher's availability.
 *
 * Rules match TeacherAvailabilityPage:
 * - unavailable: conflict if overlap
 * - available: conflict if NOT fully covered by the selected time window
 * - maybe: conflict if there is a class that day (always prompt)
 */
function isEventConflicting(params: {
  status: AvailabilityStatus;
  rangeStartMin: number;
  rangeEndMin: number;
  eventStartMin: number;
  eventEndMin: number;
}): boolean {
  const { status, rangeStartMin, rangeEndMin, eventStartMin, eventEndMin } = params;
  if (![rangeStartMin, rangeEndMin, eventStartMin, eventEndMin].every(Number.isFinite)) return false;
  if (status === 'unavailable') return overlaps(eventStartMin, eventEndMin, rangeStartMin, rangeEndMin);
  if (status === 'available') return !fullyCovered(rangeStartMin, rangeEndMin, eventStartMin, eventEndMin);
  return true; // maybe
}

export async function analyzeLeadTeacherAssignment(params: {
  squad: Squad;
  newTeacherId: string;
  overwriteAllEvents?: boolean;
}): Promise<LeadTeacherAssignmentAnalysis> {
  const { squad, newTeacherId, overwriteAllEvents = false } = params;
  const squadTz = String((squad as any)?.timezone || DEFAULT_TZ).trim() || DEFAULT_TZ;

  const teacherSnap = await getDoc(doc(db, 'users', newTeacherId));
  if (!teacherSnap.exists()) throw new Error('Teacher not found.');
  const teacher = { ...(teacherSnap.data() as any), id: teacherSnap.id } as User;

  const teacherTz = String((teacher as any)?.timeZone || (teacher as any)?.timezone || DEFAULT_TZ).trim() || DEFAULT_TZ;
  const hasWeekly = hasAnyWeeklyAvailabilityTemplate(teacher as any);

  // Load future squad events (today-forward in squad timezone)
  const todayYmd = formatTimeInZone(new Date(), squadTz, 'yyyy-MM-dd');
  const evSnap = await getDocs(query(collection(db, 'squadEvents'), where('squadId', '==', squad.id)));
  const events = evSnap.docs.map(d => {
    const data: any = d.data();
    return {
      id: d.id,
      title: String(data.title || 'Class'),
      squadId: String(data.squadId || ''),
      start: data.start?.toDate?.() || new Date(0),
      end: data.end?.toDate?.() || new Date(0),
      mainTeacherId: (typeof data.mainTeacherId === 'string' ? data.mainTeacherId : (data.mainTeacherId ?? null)) as string | null,
      mainTeacherOverridden: data.mainTeacherOverridden === true,
      syncedFromSquadDetails: data.syncedFromSquadDetails === true,
      hasActiveCoverage: Array.isArray(data.activeCoverage) && data.activeCoverage.length > 0,
    };
  });

  const oldRegularTeacher = (squad as any)?.regularTeacher ? String((squad as any).regularTeacher) : null;

  // Mirror syncSquadTeachersFromLeadChange scope: only managed-by-details, future, and not overridden.
  const candidate = events.filter(ev => {
    if (!ev.start || Number.isNaN(ev.start.getTime())) return false;
    const ymd = formatTimeInZone(ev.start, squadTz, 'yyyy-MM-dd');
    if (ymd < todayYmd) return false;
    if (overwriteAllEvents) return true;
    if (ev.hasActiveCoverage) return false;
    if (!ev.syncedFromSquadDetails) return false;
    if (ev.mainTeacherOverridden) return false;
    if (oldRegularTeacher) return ev.mainTeacherId === oldRegularTeacher;
    return !ev.mainTeacherId;
  });

  const conflictEvents: LeadTeacherConflictEvent[] = [];

  // Also treat overlaps with the teacher's existing assigned classes as conflicts ("calendar conflicts").
  // This is separate from availability templates/exceptions.
  const teacherAssigned: Array<{ id: string; squadId: string; start: Date; end: Date }> = [];
  try {
    const byMain = await getDocs(query(collection(db, 'squadEvents'), where('mainTeacherId', '==', newTeacherId)));
    const bySupport = await getDocs(query(collection(db, 'squadEvents'), where('supportingTeacherIds', 'array-contains', newTeacherId)));
    const bySubstitute = await getDocs(query(collection(db, 'squadEvents'), where('subAssignedTeacherId', '==', newTeacherId)));
    const byParticipant = await getDocs(query(collection(db, 'squadEvents'), where('eventParticipantIds', 'array-contains', newTeacherId)));
    const push = (snap: any) => {
      snap.docs.forEach((d: any) => {
        const data: any = d.data();
        const start = data.start?.toDate?.() || new Date(0);
        const end = data.end?.toDate?.() || new Date(0);
        teacherAssigned.push({ id: d.id, squadId: String(data.squadId || ''), start, end });
      });
    };
    push(byMain);
    push(bySupport);
    push(bySubstitute);
    push(byParticipant);
  } catch (e) {
    console.warn('[LeadTeacherAssignment] Failed to load teacher calendar for overlap checks', e);
  }

  for (const ev of candidate) {
    // Calendar overlap check (with other squads)
    const evStartMs = ev.start.getTime();
    const evEndMs = ev.end.getTime();
    const hasCalendarOverlap = teacherAssigned.some(other => {
      if (other.id === ev.id) return false;
      if (other.squadId === ev.squadId) return false;
      const os = other.start.getTime();
      const oe = other.end.getTime();
      if (![os, oe].every(Number.isFinite)) return false;
      return overlaps(evStartMs, evEndMs, os, oe);
    });

    const qKey = quarterKeyForDateInZone(ev.start, teacherTz);
    const dk = dateKeyForDateInZone(ev.start, teacherTz);
    const evStartMin = timeOfDayMinutesInZone(ev.start, teacherTz);
    const evEndMin = timeOfDayMinutesInZone(ev.end, teacherTz);

    const exRaw: any = (teacher as any)?.availabilityExceptions?.[qKey]?.[dk] || null;
    const wk: any = (teacher as any)?.availabilityTemplates?.[qKey]?.week || null;
    const dayKey = wk ? (dayKeyInZone(ev.start, teacherTz) as any) : null;
    const wkDay: any = (wk && dayKey) ? wk?.[dayKey] : null;

    // If there is no weekly template at all, only evaluate conflicts when an explicit exception exists.
    if (!wk && !exRaw) continue;

    const exStatus = normalizeAvailabilityStatus(exRaw?.status) || (exRaw?.type === 'unavailable' ? 'unavailable' : null);
    const wkStatus = normalizeAvailabilityStatus(wkDay?.status) || null;
    const status: AvailabilityStatus = (exStatus || wkStatus || 'unavailable') as AvailabilityStatus;

    const rangeStartStr = String(exRaw?.start || wkDay?.blocks?.[0]?.start || '00:00');
    const rangeEndStr = String(exRaw?.end || wkDay?.blocks?.[0]?.end || '23:59');
    const rangeStartMin = minutes(rangeStartStr);
    const rangeEndMin = minutes(rangeEndStr);

    const availabilityConflicts = isEventConflicting({
      status,
      rangeStartMin,
      rangeEndMin,
      eventStartMin: evStartMin,
      eventEndMin: evEndMin,
    });

    if (hasCalendarOverlap || availabilityConflicts) {
      conflictEvents.push({
        eventId: ev.id,
        squadId: ev.squadId,
        title: ev.title,
        start: ev.start,
        end: ev.end,
        dateLabel: formatTimeInZone(ev.start, squadTz, 'EEE, MMM d, yyyy'),
        teacherDateKey: dk,
        teacherQuarterKey: qKey,
      });
    }
  }

  const byDate = new Map<string, number>();
  for (const ev of conflictEvents) byDate.set(ev.dateLabel, (byDate.get(ev.dateLabel) || 0) + 1);
  const conflictsByDate = Array.from(byDate.entries())
    .map(([dateLabel, count]) => ({ dateLabel, count }))
    .sort((a, b) => a.dateLabel.localeCompare(b.dateLabel));

  return {
    teacher,
    teacherTimeZone: teacherTz,
    hasAnyWeeklyAvailabilityTemplate: hasWeekly,
    conflictEvents,
    conflictsByDate,
  };
}
